<script setup>
import { computed, h, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { DirectUpload } from 'activestorage';
import { useMapGetter } from 'dashboard/composables/store';
import { setDirectUploadAuthHeaders } from 'dashboard/helper/directUploadsHelper';
import {
  CHANNEL_POST_TYPES,
  channelTypeLabelKey,
} from 'dashboard/helper/wgptSocialMediaPosts';
import WgptSocialMediaPostsAPI from 'dashboard/api/wgpt/socialMediaPosts';
import {
  isVideoFile,
  MAX_IMAGE_COUNT,
  readVideoMetadata,
  recommendedMediaHint,
  validateFileTypeAndSize,
  validateMediaCombination,
  validateVideoSpec,
} from '../helpers/mediaSpecs';

import Button from 'next/button/Button.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import ComboBox from 'next/combobox/ComboBox.vue';
import Dialog from 'next/dialog/Dialog.vue';
import DropdownMenu from 'next/dropdown-menu/DropdownMenu.vue';
import Icon from 'next/icon/Icon.vue';
import TextArea from 'next/textarea/TextArea.vue';

defineProps({
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['create']);

const { t } = useI18n();
const dialogRef = ref(null);
const fileInput = ref(null);

const inboxesList = useMapGetter('inboxes/getInboxes');
const globalConfig = useMapGetter('globalConfig/get');

// Only inboxes for the platforms WgptSocialMediaPost#post_type supports - other
// channel types have no post_type to publish through.
const eligibleInboxes = computed(() =>
  inboxesList.value.filter(inbox => CHANNEL_POST_TYPES[inbox.channel_type])
);

const form = reactive({
  inboxId: '',
  postType: '',
  caption: '',
});

const mediaItems = ref([]);

const selectedInbox = computed(() =>
  eligibleInboxes.value.find(inbox => inbox.id === Number(form.inboxId))
);

const channelTypeText = channelType =>
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  t(channelTypeLabelKey(channelType));

const showChannelDropdown = ref(false);

const channelMenuItems = computed(() =>
  eligibleInboxes.value.map(inbox => ({
    label: inbox.name,
    value: String(inbox.id),
    action: 'select_channel',
    icon: h(ChannelIcon, { inbox, class: 'size-4' }),
    channelTypeText: channelTypeText(inbox.channel_type),
  }))
);

const selectedChannelTypeText = computed(() =>
  selectedInbox.value ? channelTypeText(selectedInbox.value.channel_type) : ''
);

const handleChannelSelect = item => {
  form.inboxId = item.value;
  showChannelDropdown.value = false;
};

const postTypeOptions = computed(() => {
  const postTypes = CHANNEL_POST_TYPES[selectedInbox.value?.channel_type] || [];
  return postTypes.map(postType => ({
    value: postType,
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    label: t(
      `WGPT_SOCIAL_MEDIA_POSTS.NEW.POST_TYPE_OPTIONS.${postType.toUpperCase()}`
    ),
  }));
});

const mediaHint = computed(() => {
  if (!form.postType) return '';

  const { imageDimensions, videoDimensions, maxDurationSeconds, maxImages } =
    recommendedMediaHint(form.postType);
  const parts = [];

  if (imageDimensions) {
    parts.push(
      maxImages
        ? t('WGPT_SOCIAL_MEDIA_POSTS.NEW.MEDIA_HINT.IMAGE_WITH_MAX', {
            dimensions: imageDimensions,
            maxImages,
          })
        : t('WGPT_SOCIAL_MEDIA_POSTS.NEW.MEDIA_HINT.IMAGE', {
            dimensions: imageDimensions,
          })
    );
  }
  if (videoDimensions) {
    parts.push(
      maxDurationSeconds
        ? t('WGPT_SOCIAL_MEDIA_POSTS.NEW.MEDIA_HINT.VIDEO_WITH_DURATION', {
            dimensions: videoDimensions,
            seconds: maxDurationSeconds,
          })
        : t('WGPT_SOCIAL_MEDIA_POSTS.NEW.MEDIA_HINT.VIDEO', {
            dimensions: videoDimensions,
          })
    );
  }

  return parts.join(' · ');
});

const fileErrorMessage = error => {
  if (!error) return '';

  const key = `WGPT_SOCIAL_MEDIA_POSTS.NEW.MEDIA_ERRORS.${error.type.toUpperCase()}`;
  return t(key, error.meta || {});
};

const mediaCombinationError = computed(() =>
  mediaItems.value.length
    ? validateMediaCombination(
        mediaItems.value.map(item => item.file),
        form.postType
      )
    : null
);

const hasFileErrors = computed(() => mediaItems.value.some(item => item.error));

const isMediaUploading = computed(() =>
  mediaItems.value.some(item => item.uploading)
);

// Once a video is attached, no other media can be added (video posts are single-file only). With
// images, more can still be added up to the platform's max image count.
const canAddMoreMedia = computed(() => {
  if (!mediaItems.value.length) return true;
  if (mediaItems.value.some(item => isVideoFile(item.file))) return false;

  const maxImages = MAX_IMAGE_COUNT[form.postType];
  return !maxImages || mediaItems.value.length < maxImages;
});

const isFormInvalid = computed(
  () =>
    !form.inboxId ||
    !form.postType ||
    !form.caption.trim() ||
    !mediaItems.value.length ||
    hasFileErrors.value ||
    isMediaUploading.value ||
    !!mediaCombinationError.value
);

const isFacebookPost = computed(
  () =>
    form.inboxId &&
    CHANNEL_POST_TYPES[selectedInbox.value?.channel_type]?.includes(
      'facebook_post'
    )
);

// Async callbacks always work on the item object itself, never on its index - the user can
// remove entries while uploads/validations are in flight, which shifts every index after it.
const validateVideoItem = async item => {
  if (!item || item.error || !isVideoFile(item.file)) return;

  try {
    const metadata = await readVideoMetadata(item.file);
    item.error = validateVideoSpec(metadata, form.postType);
  } catch {
    item.error = { type: 'unreadable_video' };
  }
};

watch(
  () => form.inboxId,
  () => {
    form.postType =
      postTypeOptions.value.length === 1 ? postTypeOptions.value[0].value : '';
  }
);

watch(
  () => form.postType,
  async postType => {
    mediaItems.value.forEach(item => {
      item.error = validateFileTypeAndSize(item.file, postType);
    });
    await Promise.all(mediaItems.value.map(item => validateVideoItem(item)));
  }
);

const handleFileClick = () => fileInput.value?.click();

const uploadFile = item => {
  const upload = new DirectUpload(
    item.file,
    WgptSocialMediaPostsAPI.directUploadUrl,
    {
      directUploadWillCreateBlobWithXHR: xhr => {
        setDirectUploadAuthHeaders(xhr);
      },
    }
  );

  upload.create((error, blob) => {
    item.uploading = false;
    if (error) {
      item.error = { type: 'upload_failed' };
    } else {
      item.blobSignedId = blob.signed_id;
    }
  });
};

const handleFileChange = async () => {
  const newFiles = Array.from(fileInput.value?.files || []);
  fileInput.value.value = null;

  // With direct uploads disabled on the installation, files aren't uploaded ahead of
  // time - the raw File objects are submitted with the create request instead.
  const useDirectUploads = globalConfig.value.directUploadsEnabled;
  const newItems = newFiles.map(file =>
    reactive({
      file,
      error: validateFileTypeAndSize(file, form.postType),
      uploading: useDirectUploads,
      blobSignedId: null,
    })
  );
  mediaItems.value.push(...newItems);

  newItems.forEach(item => {
    if (item.error) {
      item.uploading = false;
    } else if (useDirectUploads) {
      uploadFile(item);
    }
  });

  await Promise.all(newItems.map(item => validateVideoItem(item)));
};

const removeFile = index => {
  mediaItems.value.splice(index, 1);
};

const resetForm = () => {
  form.inboxId = '';
  form.postType = '';
  form.caption = '';
  mediaItems.value = [];
};

const open = () => {
  dialogRef.value?.open();
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const onSuccess = () => {
  resetForm();
  closeDialog();
};

const handleConfirm = () => {
  if (isFormInvalid.value) return;

  emit('create', {
    inbox_id: form.inboxId,
    post_type: form.postType,
    caption: form.caption.trim(),
    media: mediaItems.value.map(item => item.blobSignedId || item.file),
  });
};

defineExpose({ dialogRef, onSuccess, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    overflow-y-auto
    :title="t('WGPT_SOCIAL_MEDIA_POSTS.NEW.TITLE')"
    :confirm-button-label="t('WGPT_SOCIAL_MEDIA_POSTS.NEW.ACTIONS.CREATE')"
    :is-loading="isLoading"
    :disable-confirm-button="isFormInvalid || isLoading"
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WGPT_SOCIAL_MEDIA_POSTS.NEW.FIELDS.CHANNEL') }}
        </label>
        <div
          v-on-click-outside="() => (showChannelDropdown = false)"
          class="relative"
        >
          <button
            type="button"
            class="flex items-center justify-between w-full gap-2 px-3 py-2.5 rounded-lg bg-transparent outline outline-1 outline-n-weak hover:outline-n-slate-6 disabled:cursor-not-allowed"
            :class="{ '!outline-n-brand': showChannelDropdown }"
            :disabled="isLoading"
            @click="showChannelDropdown = !showChannelDropdown"
          >
            <span v-if="selectedInbox" class="flex items-center min-w-0 gap-2">
              <ChannelIcon :inbox="selectedInbox" class="size-4 shrink-0" />
              <span class="text-sm truncate text-n-slate-12">
                {{ selectedInbox.name }}
              </span>
              <span class="text-xs shrink-0 text-n-slate-11">
                {{ selectedChannelTypeText }}
              </span>
            </span>
            <span v-else class="text-sm text-n-slate-10">
              {{ t('WGPT_SOCIAL_MEDIA_POSTS.NEW.FIELDS.CHANNEL_PLACEHOLDER') }}
            </span>
            <Icon
              :icon="
                showChannelDropdown
                  ? 'i-lucide-chevron-up'
                  : 'i-lucide-chevron-down'
              "
              class="size-4 shrink-0 text-n-slate-11"
            />
          </button>
          <DropdownMenu
            v-if="showChannelDropdown"
            :menu-items="channelMenuItems"
            class="top-full mt-1 w-full z-50 max-h-60 overflow-y-auto"
            @action="handleChannelSelect"
          >
            <template #label="{ item }">
              <span
                class="flex items-center justify-between flex-1 min-w-0 gap-2"
              >
                <span class="text-sm truncate text-n-slate-12">
                  {{ item.label }}
                </span>
                <span class="text-xs shrink-0 text-n-slate-11">
                  {{ item.channelTypeText }}
                </span>
              </span>
            </template>
          </DropdownMenu>
        </div>
      </div>

      <div v-if="isFacebookPost" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WGPT_SOCIAL_MEDIA_POSTS.NEW.FIELDS.POST_TYPE') }}
        </label>
        <ComboBox
          v-model="form.postType"
          :options="postTypeOptions"
          :placeholder="
            t('WGPT_SOCIAL_MEDIA_POSTS.NEW.FIELDS.POST_TYPE_PLACEHOLDER')
          "
          :disabled="isLoading || !form.inboxId"
          class="w-full"
        />
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex flex-col gap-0.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WGPT_SOCIAL_MEDIA_POSTS.NEW.FIELDS.MEDIA') }}
          </label>
          <span v-if="mediaHint" class="text-xs text-n-slate-11">
            {{ mediaHint }}
          </span>
        </div>
        <ul v-if="mediaItems.length" class="flex flex-col gap-1 list-none">
          <li
            v-for="(item, index) in mediaItems"
            :key="`${item.file.name}-${index}`"
            class="flex items-center justify-between gap-2 px-2 py-1 rounded-md bg-n-alpha-2"
          >
            <div class="flex flex-col min-w-0">
              <span class="text-sm truncate text-n-slate-12">
                {{ item.file.name }}
              </span>
              <span v-if="item.error" class="text-xs text-n-ruby-9">
                {{ fileErrorMessage(item.error) }}
              </span>
              <span v-else-if="item.uploading" class="text-xs text-n-slate-11">
                {{ t('WGPT_SOCIAL_MEDIA_POSTS.NEW.MEDIA_UPLOADING') }}
              </span>
            </div>
            <Button
              icon="i-lucide-x"
              color="slate"
              variant="ghost"
              size="xs"
              class="flex-shrink-0"
              :disabled="isLoading"
              @click="removeFile(index)"
            />
          </li>
        </ul>
        <span v-if="mediaCombinationError" class="text-xs text-n-ruby-9">
          {{ fileErrorMessage(mediaCombinationError) }}
        </span>
        <Button
          v-if="canAddMoreMedia"
          :label="t('WGPT_SOCIAL_MEDIA_POSTS.NEW.FIELDS.ADD_MEDIA')"
          icon="i-lucide-upload"
          color="slate"
          variant="ghost"
          size="sm"
          class="!w-fit"
          :disabled="isLoading || !canAddMoreMedia || !form.postType"
          @click="handleFileClick"
        />
      </div>

      <TextArea
        v-model="form.caption"
        :label="t('WGPT_SOCIAL_MEDIA_POSTS.NEW.FIELDS.CAPTION')"
        :placeholder="
          t('WGPT_SOCIAL_MEDIA_POSTS.NEW.FIELDS.CAPTION_PLACEHOLDER')
        "
        :disabled="isLoading"
        auto-height
      />
    </div>
    <input
      ref="fileInput"
      type="file"
      multiple
      accept="image/*,video/*"
      class="hidden"
      @change="handleFileChange"
    />
  </Dialog>
</template>
